import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getQualificationsAConfirmer, saveQualification } from '../services/api';

/**
 * Les interventions que la conduite d'activité peut sortir du calcul SLA.
 *
 * Covage admet trois situations dont le client est à l'origine, mais rien dans
 * les données ne les distingue d'un manquement de notre fait : un rendez-vous
 * décalé parce que le client l'a demandé ressemble en tout point à un
 * rendez-vous que nous n'avons pas tenu. Seule la personne qui l'a eu au
 * téléphone le sait.
 *
 * La déclaration existait déjà, dossier par dossier, depuis la fiche — à
 * condition de savoir lequel chercher. Personne ne voyait l'ensemble : 215
 * engagements E-RDV manqués sur quatre mois, et une seule déclaration
 * enregistrée. Cette page les rassemble.
 *
 * Jennifer a confirmé le 9 octobre 2026 que notre déclaration suffit, sans
 * justificatif. On conserve l'auteur et la date, pour qu'elle reste explicable.
 */

const MOIS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
                 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function moisLabel(cle) {
  const [a, m] = String(cle || '').split('-');
  if (!a || !m) return cle || '—';
  return `${MOIS_FR[Number(m) - 1]} ${a.slice(2)}`;
}

const jour = iso => String(iso || '').slice(0, 10).split('-').reverse().join('/');

// Un dépassement d'un jour relève bien plus souvent d'un décalage demandé par
// le client que d'un manquement : 47 des 215 engagements manqués sont dans ce
// cas, 75 à deux jours près. L'écart est donc la colonne de tri par défaut.
function graviteErdv(c) {
  const e = c.ecart_jours;
  if (e == null) return ['—', 'var(--text3)', 'var(--neutre)'];
  if (e <= 2) return [`+${e} j`, '#b45309', 'rgba(217,119,6,.16)'];
  if (e <= 7) return [`+${e} j`, '#c2410c', 'rgba(194,65,12,.16)'];
  return [`+${e} j`, '#b91c1c', 'rgba(212,43,43,.16)'];
}

function Pastille({ texte, couleur, fond, titre }) {
  return (
    <span title={titre} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
      whiteSpace: 'nowrap', color: couleur, background: fond }}>{texte}</span>
  );
}

/** Une ligne à passer en revue. */
function Ligne({ candidat, famille, enCours, onDeclarer, onAnnuler }) {
  const [commentaire, setCommentaire] = useState(candidat.commentaire || '');
  const [ouvert, setOuvert] = useState(false);
  const occupe = enCours === candidat.dossier_id + '|' + candidat.ref_work_order;

  // Ce qui permet de juger, propre à chaque motif.
  let preuve = null;
  if (famille.cle === 'erdv') {
    const [txt, col, bg] = graviteErdv(candidat);
    preuve = (
      <>
        <span style={{ fontSize: 12.5 }}>
          promis le <strong>{jour(candidat.date_erdv)}</strong>, réalisé le <strong>{jour(candidat.date_realisee)}</strong>
        </span>
        <Pastille texte={txt} couleur={col} fond={bg} titre="Dépassement en jours calendaires" />
      </>
    );
  } else if (famille.cle === 'delai') {
    preuve = (
      <>
        <span style={{ fontSize: 12.5 }}>
          ouvert le <strong>{jour(candidat.ouverture)}</strong>, clos le <strong>{jour(candidat.cloture)}</strong>
        </span>
        <Pastille texte={`${candidat.delai} / ${candidat.seuil} j`} couleur="#b91c1c" fond="rgba(212,43,43,.16)"
          titre={`Seuil : ${candidat.seuil} jours ${candidat.unite}`} />
        {(candidat.mesures || []).map(m => (
          <Pastille key={m} texte={m} couleur="var(--text2)" fond="var(--neutre)" />
        ))}
      </>
    );
  } else {
    preuve = (
      <>
        <span style={{ fontSize: 12.5 }}>
          CR soumis le <strong>{jour(candidat.date_soumission)}</strong>
          {candidat.resultat && <> · {candidat.resultat}</>}
        </span>
        {candidat.technicien && (
          <Pastille texte={candidat.technicien.trim()} couleur="var(--text2)" fond="var(--neutre)" />
        )}
      </>
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '11px 16px',
      background: candidat.deja ? 'rgba(22,163,74,.05)' : 'transparent' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 150 }}>
          <a href={`/dossier/${candidat.dossier_id}`} target="_blank" rel="noreferrer"
            style={{ display: 'block', fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)',
              color: 'var(--accent)', textDecoration: 'none' }}>
            {candidat.ref_commande || 'réf O inconnue'} ↗
          </a>
          <span style={{ display: 'block', fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 1 }}>
            {candidat.ref_work_order || '—'}
          </span>
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {candidat.client || <span style={{ color: 'var(--text3)', fontWeight: 400 }}>client inconnu de l’index des CR</span>}
          </div>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap', marginTop: 3 }}>
            {preuve}
            {candidat.code_postal && (
              <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{candidat.code_postal}</span>
            )}
            <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{moisLabel(candidat.mois)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexShrink: 0 }}>
          {candidat.deja ? (
            <>
              <Pastille texte="Sorti du calcul" couleur="#16a34a" fond="rgba(22,163,74,.14)" />
              <button onClick={() => onAnnuler(candidat)} disabled={occupe}
                style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 11px', borderRadius: 7,
                  background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border2)',
                  cursor: occupe ? 'default' : 'pointer', opacity: occupe ? .5 : 1 }}>
                {occupe ? '⏳…' : 'Remettre dans le calcul'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setOuvert(o => !o)}
                style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 11px', borderRadius: 7,
                  background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border2)', cursor: 'pointer' }}>
                {ouvert ? 'Sans motif' : 'Motif…'}
              </button>
              <button onClick={() => onDeclarer(candidat, commentaire)} disabled={occupe}
                style={{ font: 'inherit', fontSize: 12, fontWeight: 700, padding: '6px 13px', borderRadius: 7,
                  background: 'var(--blue)', color: '#fff', border: '1px solid var(--blue)',
                  cursor: occupe ? 'default' : 'pointer', opacity: occupe ? .5 : 1, whiteSpace: 'nowrap' }}>
                {occupe ? '⏳…' : 'Sortir du calcul'}
              </button>
            </>
          )}
        </div>
      </div>

      {ouvert && !candidat.deja && (
        <input
          autoFocus
          value={commentaire}
          onChange={e => setCommentaire(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onDeclarer(candidat, commentaire); }}
          placeholder="Ce que le client a demandé — facultatif, mais c’est ce qui rendra la déclaration explicable"
          style={{ width: '100%', marginTop: 9, font: 'inherit', fontSize: 12.5, padding: '8px 11px',
            borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--bg)',
            color: 'var(--text)', boxSizing: 'border-box' }}
        />
      )}

      {candidat.deja && (candidat.commentaire || candidat.par) && (
        <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 6, lineHeight: 1.45 }}>
          {candidat.commentaire && <span style={{ color: 'var(--text2)' }}>« {candidat.commentaire} » </span>}
          {candidat.par && <>déclaré par {candidat.par}{candidat.le && ` le ${new Date(candidat.le).toLocaleDateString('fr-FR')}`}</>}
        </div>
      )}
    </div>
  );
}

export default function Qualifications() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [famille, setFamille] = useState('erdv');
  const [mois, setMois] = useState('tous');
  const [voirDeclares, setVoirDeclares] = useState(false);
  const [tri, setTri] = useState('ecart');
  const [enCours, setEnCours] = useState(null);
  const [msg, setMsg] = useState('');

  const charger = useCallback(async (force = false) => {
    setLoading(true); setErreur(null);
    try {
      setData(await getQualificationsAConfirmer(force ? { refresh: 1 } : {}));
    } catch (e) {
      setErreur(e.response?.data?.error || 'Lecture impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const f = useMemo(
    () => (data?.familles || []).find(x => x.cle === famille) || null,
    [data, famille]);

  const visibles = useMemo(() => {
    if (!f) return [];
    let l = f.candidats.filter(c => (mois === 'tous' || c.mois === mois) && (voirDeclares || !c.deja));
    if (tri === 'ecart' && f.cle === 'erdv') l = [...l].sort((a, b) => (a.ecart_jours ?? 999) - (b.ecart_jours ?? 999));
    else if (tri === 'ecart' && f.cle === 'delai') l = [...l].sort((a, b) => (b.delai - b.seuil) - (a.delai - a.seuil));
    else l = [...l].sort((a, b) => String(b.mois).localeCompare(String(a.mois)));
    return l;
  }, [f, mois, voirDeclares, tri]);

  // La déclaration écrit tout de suite, et la ligne change sous la main : la
  // recharger entièrement ferait perdre la place dans une liste de 215.
  const ecrire = async (candidat, valeur, commentaire) => {
    const cle = candidat.dossier_id + '|' + candidat.ref_work_order;
    setEnCours(cle); setMsg('');
    try {
      await saveQualification({
        dossierId: candidat.dossier_id,
        refWorkOrder: candidat.ref_work_order,
        [f.champ]: valeur,
        commentaire: commentaire !== undefined ? commentaire : (candidat.commentaire || ''),
      });
      setData(d => ({
        ...d,
        familles: d.familles.map(fam => fam.cle !== f.cle ? fam : {
          ...fam,
          candidats: fam.candidats.map(c =>
            (c.dossier_id + '|' + c.ref_work_order) === cle
              ? { ...c, deja: valeur, commentaire: commentaire ?? c.commentaire }
              : c),
          par_mois: fam.par_mois.map(m => m.mois !== candidat.mois ? m
            : { ...m, declares: m.declares + (valeur ? 1 : -1) }),
        }),
      }));
      setMsg(valeur
        ? `✓ ${candidat.ref_commande || candidat.ref_work_order} sorti du calcul`
        : `✓ ${candidat.ref_commande || candidat.ref_work_order} remis dans le calcul`);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('✗ ' + (e.response?.data?.error || e.message));
    } finally { setEnCours(null); }
  };

  const onglet = (fam) => {
    const reste = fam.candidats.filter(c => !c.deja).length;
    const actif = famille === fam.cle;
    return (
      <button key={fam.cle} onClick={() => { setFamille(fam.cle); setMois('tous'); }}
        style={{
          flex: 1, textAlign: 'left', font: 'inherit', cursor: 'pointer', color: 'inherit',
          background: actif ? 'rgba(37,99,235,.09)' : 'transparent',
          border: 0, borderBottom: `3px solid ${actif ? 'var(--blue2)' : 'transparent'}`,
          padding: '13px 16px 14px', display: 'flex', flexDirection: 'column', gap: 2,
        }}>
        <span style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.1,
          color: reste ? 'var(--text)' : 'var(--text3)' }}>{reste}</span>
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{fam.titre}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{fam.indicateur}</span>
      </button>
    );
  };

  const restant = f ? f.candidats.filter(c => !c.deja).length : 0;
  const declares = f ? f.candidats.filter(c => c.deja).length : 0;

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Qualifications à confirmer</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {loading ? 'Lecture des historiques…'
              : f ? <><strong style={{ color: 'var(--text)' }}>{restant}</strong> à passer en revue
                  {declares > 0 && <> · {declares} déjà sorti{declares > 1 ? 's' : ''} du calcul</>}</>
              : 'Rien à afficher'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => charger(true)} disabled={loading}>
            {loading ? '⏳' : '↻ Recalculer'}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px 36px', background: 'var(--bg)' }}>

        {erreur && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
            background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{erreur}</div>
        )}
        {msg && (
          <div style={{ padding: '9px 13px', borderRadius: 8, marginBottom: 14, fontSize: 12.5, fontWeight: 600,
            background: msg.startsWith('✓') ? '#f0fdf4' : '#fef2f2',
            color: msg.startsWith('✓') ? '#16a34a' : '#ef4444',
            border: `1px solid ${msg.startsWith('✓') ? '#bbf7d0' : '#fecaca'}` }}>{msg}</div>
        )}

        {loading && !data && (
          <div style={{ padding: '50px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            Parcours des historiques d’étapes…
          </div>
        )}

        {data && (
          <>
            <div style={{ display: 'flex', background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
              {data.familles.map(onglet)}
            </div>

            {f && (
              <>
                <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--amber2)', borderRadius: 10, padding: '12px 15px', marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em',
                    textTransform: 'uppercase', color: '#f59e0b' }}>
                    Ce que Covage admet
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 4, lineHeight: 1.55 }}>
                    {f.regle}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
                    Notre déclaration suffit, sans justificatif — confirmé par Jennifer le 9 octobre 2026.
                    L’auteur et la date sont conservés pour qu’elle reste explicable.
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {[['tous', 'Tous les mois', f.candidats.filter(c => !c.deja).length],
                      ...f.par_mois.map(m => [m.mois, moisLabel(m.mois), m.total - m.declares])]
                      .map(([v, lbl, n]) => (
                        <button key={v} onClick={() => setMois(v)} style={{
                          font: 'inherit', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                          background: mois === v ? 'rgba(37,99,235,.14)' : 'var(--bg2)',
                          color: mois === v ? 'var(--blue2)' : 'var(--text2)',
                          border: `1px solid ${mois === v ? 'var(--blue2)' : 'var(--border)'}`,
                        }}>
                          {lbl}<span style={{ fontSize: 10.5, fontWeight: 800, opacity: .75 }}>{n}</span>
                        </button>
                      ))}
                  </div>

                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
                    {f.cle !== 'cri' && (
                      <button onClick={() => setTri(t => t === 'ecart' ? 'mois' : 'ecart')}
                        style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7,
                          background: 'var(--bg2)', color: 'var(--text2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                        Trier : {tri === 'ecart' ? 'plus petit dépassement' : 'plus récent'}
                      </button>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, cursor: 'pointer', color: 'var(--text2)' }}>
                      <input type="checkbox" checked={voirDeclares} onChange={e => setVoirDeclares(e.target.checked)}
                        style={{ width: 15, height: 15, accentColor: 'var(--blue)' }} />
                      Voir les {declares} déjà sorti{declares > 1 ? 's' : ''}
                    </label>
                  </div>
                </div>

                {f.cle === 'erdv' && tri === 'ecart' && visibles.length > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 10, lineHeight: 1.5,
                    maxWidth: '100ch' }}>
                    Les plus petits dépassements d’abord : un rendez-vous manqué d’un jour relève bien plus
                    souvent d’un décalage demandé par le client que d’un manquement.{' '}
                    {f.candidats.filter(c => (c.ecart_jours ?? 99) <= 2).length} des {f.candidats.length} engagements
                    sont dans ce cas.
                  </div>
                )}

                <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  {visibles.length === 0 ? (
                    <div style={{ padding: '26px 18px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                      {f.candidats.length === 0
                        ? 'Aucune intervention de ce type sur la période.'
                        : voirDeclares
                          ? 'Rien à ce filtre.'
                          : 'Tout a été passé en revue sur ce filtre.'}
                    </div>
                  ) : visibles.map(c => (
                    <Ligne
                      key={c.dossier_id + '|' + c.ref_work_order}
                      candidat={c}
                      famille={f}
                      enCours={enCours}
                      onDeclarer={(cand, com) => ecrire(cand, true, com)}
                      onAnnuler={cand => ecrire(cand, false)}
                    />
                  ))}
                </div>

                <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border)',
                  fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.65, maxWidth: '100ch' }}>
                  <strong style={{ color: 'var(--text2)' }}>Sortir du calcul retire du dénominateur.</strong>{' '}
                  L’intervention ne compte plus ni comme respectée ni comme manquée : le taux est calculé sur
                  les engagements restants. C’est la lecture de Covage, et c’est pourquoi une déclaration de
                  complaisance se verrait — le nombre d’engagements mesurés baisserait avec le taux qui monte.
                  <br /><br />
                  <strong style={{ color: 'var(--text2)' }}>Une réf O manquante n’empêche pas de décider.</strong>{' '}
                  Elle vient de l’index des comptes rendus, qui ne connaît pas les dossiers n’ayant jamais eu de
                  CR soumis — 34 des 215 engagements E-RDV. Le lien ouvre la fiche, qui porte tout.
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
